import { Box, Flex, Icon, Text, HStack } from '@chakra-ui/react';
import { BranchDto, CompanyDto } from '../../lib/api';
import { LinkButton } from '@/components/ui/link-button';
import { Building, ChevronUp, MapPin, Plus } from 'lucide-react';
import { useLoaderData } from 'react-router-dom';

const BranchAddLocation = ({ company }: { company: { id: string } }) => {
    return (
        <Flex
            bg={{ base: 'white', _dark: 'musa.dark.500' }}
            borderBottomRadius="10"
            border="solid"
            borderWidth="1px"
            borderTop="none"
            py="8px"
            px="12px"
            gap="8px"
            alignItems="center"
            borderColor={{ base: 'musa.light.border', _dark: 'musa.dark.border' }}
            borderTopWidth="1px">
            <LinkButton to={`/company/${company.id}/branch/create`} variant="plain" size="sm">
                <HStack alignItems="center">
                    <Icon boxSize="3" color={{ base: 'purple.600', _dark: 'white' }} fontWeight="bold">
                        <Plus />
                    </Icon>
                    <Text color={{ base: 'purple.600', _dark: 'white' }} fontWeight="600" textAlign="end" fontSize="sm">
                        Add Branch
                    </Text>
                </HStack>
            </LinkButton>
        </Flex>
    );
};

const CompanyBranches = ({ branch, company, index }: { branch: BranchDto; company: CompanyDto; index: number }) => {
    return (
        <>
            <Flex
                key={branch.id}
                bg={{ base: 'white', _dark: 'musa.dark.500' }}
                borderColor={{ base: 'musa.light.border', _dark: 'musa.dark.border' }}
                borderLeftWidth="1px"
                borderRightWidth="1px"
                borderBottomWidth="1px"
                py="8px"
                px="12px"
                gap="8px"
                alignItems="center">
                <Icon boxSize="6" alignSelf="start">
                    <MapPin />
                </Icon>
                <Box>
                    <Text fontWeight="500">{branch.name}</Text>
                    {branch.address && (
                        <Text fontWeight="500" color={{ base: 'gray.600', _dark: 'white' }}>
                            {branch.address.address} {branch.address.city} {branch.address.state} {branch.address.zipcode}
                        </Text>
                    )}
                </Box>
                <LinkButton to={`/company/${company.id}/branch/${branch.id}`} ml="auto" variant="plain" size="sm">
                    <Icon
                        boxSize={6}
                        transform="rotate(90deg)"
                        _hover={{
                            cursor: 'pointer',
                        }}>
                        <ChevronUp />
                    </Icon>
                </LinkButton>
            </Flex>
            {index === company.branches.length - 1 && <BranchAddLocation company={{ id: company.id }} />}
        </>
    );
};

const CompanyDetails = ({ company, index }: { company: CompanyDto; index: number }) => {
    return (
        <Flex
            bg={{ base: '#eae9eb', _dark: 'musa.dark.400' }}
            overflow="hidden"
            alignItems="center"
            py="8px"
            px="12px"
            gap="8px"
            mt={index !== 0 ? 4 : 0}
            borderLeftWidth="1px"
            borderRightWidth="1px"
            borderBottomWidth="none"
            borderTopRadius="10"
            borderColor={{ base: 'musa.light.border', _dark: 'musa.dark.border' }}>
            <Icon boxSize="6">
                <Building />
            </Icon>
            <Text fontSize="lg" fontWeight="500">
                {company.name}
            </Text>
            <LinkButton to={`/company/${company.id}`} ml="auto" variant="plain" size="sm">
                <Icon
                    boxSize={6}
                    transform="rotate(90deg)"
                    _hover={{
                        cursor: 'pointer',
                    }}>
                    <ChevronUp />
                </Icon>
            </LinkButton>
        </Flex>
    );
};

export function OrganizationOverviewPage() {
    const data = useLoaderData() as CompanyDto[];

    return (
        <Box>
            {data?.map((company, indx) => {
                return (
                    <Box key={company.id}>
                        <CompanyDetails company={company} index={indx} />
                        {company.branches.map((branch, branchIndx) => (
                            <CompanyBranches key={branch.id} branch={branch} company={company} index={branchIndx} />
                        ))}
                        {!company.branches.length && <BranchAddLocation company={{ id: company.id }} />}
                    </Box>
                );
            })}

            <Box mt="8px">
                <LinkButton to={`/company/create`} variant="plain" size="sm">
                    <HStack>
                        <Icon color={{ base: '#6e6c76', _dark: '#cac8cf' }} fontWeight="bold">
                            <Plus />
                        </Icon>
                        <Text color={{ base: '#6e6c76', _dark: '#cac8cf' }} fontWeight="600">
                            Add New Company
                        </Text>
                    </HStack>
                </LinkButton>
            </Box>
        </Box>
    );
}
